import { UserProfileType, UserType } from "_interfaces";
import { API_URL } from "_lib/utils";
import { UserAtom } from "_states";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useRecoilState, useResetRecoilState } from "recoil";

export const useUserActions = () => {
  const baseUrl = `${API_URL}`;
  const [user, setUser] = useRecoilState(UserAtom);
  const navigate = useNavigate();

  const register = async ({
    firstName,
    lastName,
    username,
    password,
  }: {
    firstName: string;
    lastName: string;
    username: string;
    password: string;
  }) => {
    return axios.post(`${baseUrl}/auth/register`, {
      firstName,
      lastName,
      username,
      password,
    });
  };

  const forgotPassword = ({ username }: { username: string }) => {
    return axios.post(`${baseUrl}/auth/forgot-password`, {
      username,
    });
  };

  const resetPassword = ({
    password,
    token,
  }: {
    password: string;
    token: string;
  }) => {
    return axios.post(`${baseUrl}/auth/reset-password`, {
      password,
      token,
    });
  };

  const login = async ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }) => {
    return new Promise((resolve, reject) => {
      axios
        .post(`${baseUrl}/auth/login`, {
          username,
          password,
        })
        .then(
          (response) => {
            if (response.status <= 300) {
              const user: UserType = {
                userid: response.data.id,
                username: response.data.email,
                token: response.data.token,
                sctoken: response.data.sctoken,
                scrole: response.data.scrole,
                role: response.data.role,
                name: `${response.data.firstName ?? "User"} ${
                  response.data.lastName ?? ""
                }`,
              };
              localStorage.setItem("user", JSON.stringify(user));
              setUser(user);
              resolve(response);
            } else {
              reject(response);
            }
          },
          (error) => {
            reject(error);
          }
        );
    });
  };

  const update = async (userInput: UserProfileType) => {
    try {
      const response = await axios.put(`${baseUrl}/user`, userInput, {
        headers: { "x-token": user.token },
      });

      return {
        error: false,
        data: response.data,
      };
    } catch (error) {
      return {
        error,
        data: false,
      };
    }
  };

  const getProfile = async () => {
    try {
      const response = await axios.get(`${baseUrl}/user`, {
        headers: { "X-Token": user.token },
      });

      return {
        error: false,
        data: response.data,
      };
    } catch (error) {
      return {
        error,
        data: false,
      };
    }
  };

  const connectBroker = async () => {
    try {
      const response = await axios.get(`${baseUrl}/user/connect`, {
        headers: { "X-Token": user.token },
      });

      return {
        error: false,
        data: response.data,
      };
    } catch (error) {
      return {
        error,
        data: false,
      };
    }
  };
  const connectBrokerComplete = async (transactionId: string) => {
    try {
      const response = await axios.get(
        `${baseUrl}/user/complete-connect?transactionId=${transactionId}`,
        {
          headers: { "X-Token": user.token },
        }
      );

      return {
        error: false,
        data: response.data,
      };
    } catch (error) {
      return {
        error,
        data: false,
      };
    }
  };

  const getInvestments = async () => {
    return axios.get(`${baseUrl}/user/investments`, {
      headers: { "X-Token": user.token },
    });
  };

  const changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      const response = await axios.put(
        `${baseUrl}/user/password`,
        { oldPassword, newPassword },
        {
          headers: { "X-Token": user.token },
        }
      );

      return {
        error: false,
        data: response.data,
      };
    } catch (error) {
      return {
        error,
        data: false,
      };
    }
  };

  const resetUser = useResetRecoilState(UserAtom);

  const logout = () => {
    // remove user from local storage, set User state to null and redirect to login page
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    resetUser();
    navigate("/auth/login", { replace: true });
  };

  return {
    register,
    forgotPassword,
    resetPassword,
    login,
    logout,
    changePassword,
    update,
    getProfile,
    connectBroker,
    connectBrokerComplete,
    getInvestments,
    resetUser,
  };
};
